import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  WebsiteSiteInput,
  WebsiteVisitIntent,
  WebsiteVisitorPrefsInput,
} from "@sales-ops-amplify/shared";
import {
  createWebsiteSite,
  deleteWebsiteSite,
  getVisitorAnalytics,
  getVisitorCompany,
  getVisitorPerson,
  getVisitorPreferences,
  getWebsiteScript,
  listVisitorCompanies,
  listVisitorPeople,
  listWebsiteSites,
  seedVisitorSamples,
  testWebsiteConnection,
  updateVisitorPreferences,
  updateWebsiteSite,
} from "@/api/visitors";

export function useWebsiteSites() {
  return useQuery({
    queryKey: ["visitors", "sites"],
    queryFn: listWebsiteSites,
  });
}

export function useVisitorCompanies(params?: {
  siteId?: string;
  dateFrom?: string;
  intent?: WebsiteVisitIntent;
  q?: string;
}) {
  return useQuery({
    queryKey: ["visitors", "companies", params],
    queryFn: () => listVisitorCompanies(params),
  });
}

export function useVisitorPeople(params?: {
  siteId?: string;
  dateFrom?: string;
  intent?: WebsiteVisitIntent;
  q?: string;
}) {
  return useQuery({
    queryKey: ["visitors", "people", params],
    queryFn: () => listVisitorPeople(params),
  });
}

export function useVisitorAnalytics(params?: {
  siteId?: string;
  dateFrom?: string;
}) {
  return useQuery({
    queryKey: ["visitors", "analytics", params],
    queryFn: () => getVisitorAnalytics(params),
  });
}

export function useVisitorPreferences() {
  return useQuery({
    queryKey: ["visitors", "preferences"],
    queryFn: getVisitorPreferences,
  });
}

export function useVisitorCompanyDetail(key: string | undefined) {
  return useQuery({
    queryKey: ["visitors", "company", key],
    queryFn: () => getVisitorCompany(key!),
    enabled: Boolean(key),
  });
}

export function useVisitorPersonDetail(leadId: string | undefined) {
  return useQuery({
    queryKey: ["visitors", "person", leadId],
    queryFn: () => getVisitorPerson(leadId!),
    enabled: Boolean(leadId),
  });
}

export function useWebsiteScript(siteId: string | undefined) {
  return useQuery({
    queryKey: ["visitors", "script", siteId],
    queryFn: () => getWebsiteScript(siteId!),
    enabled: Boolean(siteId),
  });
}

export function useCreateWebsiteSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WebsiteSiteInput) => createWebsiteSite(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["visitors", "sites"] });
    },
  });
}

export function useUpdateWebsiteSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      siteId,
      patch,
    }: {
      siteId: string;
      patch: Partial<WebsiteSiteInput>;
    }) => updateWebsiteSite(siteId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["visitors", "sites"] });
    },
  });
}

export function useDeleteWebsiteSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (siteId: string) => deleteWebsiteSite(siteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["visitors", "sites"] });
    },
  });
}

export function useTestWebsiteConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (siteId: string) => testWebsiteConnection(siteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["visitors", "sites"] });
    },
  });
}

export function useUpdateVisitorPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WebsiteVisitorPrefsInput) =>
      updateVisitorPreferences(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["visitors", "preferences"] });
    },
  });
}

export function useSeedVisitorSamples() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => seedVisitorSamples(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["visitors"] });
    },
  });
}
